<template>
  <div class="w-full px-4 sm:px-6 lg:px-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">Entry Log</h1>
      </div>
    </div>

    <div class="mt-8 flex flex-col">
      <!-- Tab Segmented Control -->
      <div
        class="mb-4 flex w-full flex-wrap items-center justify-between gap-2"
      >
        <BaseSegmentedControl
          v-if="tabOptions.length > 1"
          v-model="activeTab"
          :options="tabOptions"
        />
      </div>

      <!-- Content based on active tab -->
      <template v-if="activeTab === 'anpr'">
        <CameraEventsTable v-model:date-range="datePickerModel" />
      </template>

      <template v-else-if="activeTab === 'keypad'">
        <KeypadEventsTable v-model:date-range="datePickerModel" />
      </template>

      <template v-else-if="activeTab === 'storage'">
        <StorageCrossingsTable v-model:date-range="datePickerModel" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
const route = useRoute();
const router = useRouter();

// Tab state lives in the URL: /entry-log/anpr, /entry-log/keypad and
// /entry-log/storage (the compound gates).
const ROUTE_FOR_TAB = {
  anpr: "cameraEvents",
  keypad: "entryLogKeypad",
  storage: "entryLogStorage",
} as const;
type Tab = keyof typeof ROUTE_FOR_TAB;

const activeTab = computed({
  get: (): Tab =>
    route.name === "entryLogKeypad"
      ? "keypad"
      : route.name === "entryLogStorage"
        ? "storage"
        : "anpr",
  set: (tab: Tab) => router.push({ name: ROUTE_FOR_TAB[tab] }),
});

const isDigitalArrivals = useIsDigitalArrivals();
const isKeypad = useIsKeypad();
const { data: sites } = useSites();
const { id: currentSiteId } = useCurrentSite();
// On "All sites", shown when any site has a compound.
const hasStorageCompound = computed(() =>
  (sites.value ?? []).some(
    (site) =>
      site.hasStorageCompound &&
      (currentSiteId.value == null || site.id === currentSiteId.value),
  ),
);

const tabOptions = computed(() => [
  ...(isDigitalArrivals.value ? [{ label: "ANPR", value: "anpr" }] : []),
  ...(isKeypad.value ? [{ label: "Keypad", value: "keypad" }] : []),
  ...(hasStorageCompound.value ? [{ label: "Storage", value: "storage" }] : []),
]);

// Direct URLs can land on a tab the site's feature flags hide: bounce to the
// first enabled one (or Bookings when the site has none). No-op until the
// sites query resolves, or a hard reload would bounce legitimate users.
watchEffect(() => {
  if (!sites.value) return;
  if (tabOptions.value.some(({ value }) => value === activeTab.value)) return;
  const first = tabOptions.value[0]?.value as Tab | undefined;
  router.replace({ name: first ? ROUTE_FOR_TAB[first] : "bookings" });
});

// Every tab shares one range (defaults to today), so switching tabs — which
// remounts the table — keeps whatever the user picked. Each table owns its
// own status/search controls.
const { datePickerModel } = useDatePickerContext();
</script>
